import { useEffect, useState } from "react";
import { FileText, Loader2 } from "lucide-react";
import { cn } from "../common/cn";
import { fetchDebatePersonas } from "../../transport/authClient";
import type { DebatePersonaSnapshot } from "../../domain/types";

interface Props {
  debateAId: string;
  debateBId: string;
  labelA?: string;
  labelB?: string;
}

function diffLines(
  a: string,
  b: string,
): Array<{ type: "same" | "add" | "remove"; text: string }> {
  const linesA = a.split("\n");
  const linesB = b.split("\n");
  const maxLen = Math.max(linesA.length, linesB.length);
  const result: Array<{ type: "same" | "add" | "remove"; text: string }> = [];

  for (let i = 0; i < maxLen; i++) {
    const la = linesA[i];
    const lb = linesB[i];
    if (la === lb) {
      result.push({ type: "same", text: la ?? "" });
    } else {
      if (la !== undefined) result.push({ type: "remove", text: la });
      if (lb !== undefined) result.push({ type: "add", text: lb });
    }
  }
  return result;
}

const LINE_COLORS = {
  same: "",
  add: "bg-emerald-500/10 text-emerald-300",
  remove: "bg-red-500/10 text-red-300 line-through",
};

export default function PersonaDiffView({
  debateAId,
  debateBId,
  labelA = "A",
  labelB = "B",
}: Props) {
  const [personasA, setPersonasA] = useState<DebatePersonaSnapshot[]>([]);
  const [personasB, setPersonasB] = useState<DebatePersonaSnapshot[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchDebatePersonas(debateAId),
      fetchDebatePersonas(debateBId),
    ])
      .then(([a, b]) => {
        setPersonasA(a);
        setPersonasB(b);
      })
      .finally(() => setLoading(false));
  }, [debateAId, debateBId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-fg-muted">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Persona verileri yükleniyor…
      </div>
    );
  }

  const maxSeats = Math.max(personasA.length, personasB.length);

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-fg-strong flex items-center gap-1.5">
        <FileText className="h-4 w-4 text-accent-400" />
        Persona Prompt Karşılaştırması
      </h3>

      {Array.from({ length: maxSeats }, (_, idx) => {
        const pa = personasA.find((p) => p.seat_index === idx);
        const pb = personasB.find((p) => p.seat_index === idx);
        const nameA = pa?.display_name ?? "—";
        const nameB = pb?.display_name ?? "—";
        const instrA = pa?.system_instr ?? "";
        const instrB = pb?.system_instr ?? "";
        const changed = instrA !== instrB;

        return (
          <div
            key={idx}
            className={cn(
              "rounded-lg border p-3",
              changed
                ? "border-yellow-500/30 bg-yellow-500/5"
                : "border-line-soft/30 bg-surface-1/50",
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="font-medium text-fg-strong">
                  Koltuk {idx}
                </span>
                {changed && (
                  <span className="rounded-full bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 text-[10px] font-medium">
                    Değiştirildi
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-[10px] text-fg-muted">
                <span className="text-blue-400">
                  {labelA}: {nameA}
                </span>
                <span className="text-emerald-400">
                  {labelB}: {nameB}
                </span>
              </div>
            </div>

            {changed ? (
              <div className="rounded bg-surface-2/40 p-2 font-mono text-[11px] leading-relaxed overflow-x-auto max-h-48 overflow-y-auto">
                {diffLines(instrA, instrB).map((line, li) => (
                  <div
                    key={li}
                    className={cn("px-1 rounded-sm", LINE_COLORS[line.type])}
                  >
                    <span className="select-none text-fg-muted/40 mr-2">
                      {line.type === "remove"
                        ? "−"
                        : line.type === "add"
                          ? "+"
                          : " "}
                    </span>
                    {line.text || "\u00A0"}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[11px] text-fg-muted/60 italic">
                Prompt değiştirilmemiş.
              </p>
            )}

            {pa && pb && pa.model_id !== pb.model_id && (
              <div className="mt-1.5 text-[10px] text-fg-muted">
                Model:{" "}
                <span className="text-blue-400">
                  {pa.model_id ?? "varsayılan"}
                </span>
                {" → "}
                <span className="text-emerald-400">
                  {pb.model_id ?? "varsayılan"}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
